import React, { useState, useEffect } from 'react';
import { StaffApis } from 'apis/staff';
import Table, { Column } from 'ui/Table';
import Button from 'ui/Button';
import Input from 'ui/Input';
import Modal from 'ui/Modal';
import Loading from 'ui/Loading';
import StatCard from 'ui/StatCard';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import StatsGrid from 'ui/StatsGrid';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import { toast } from 'react-toastify';
import moment from 'moment';
import './style.scss';

interface AnnualLeaveData {
    id: number;
    name: string;
    position: string;
    join_date: string;
    annual_leave_generated: number;
    annual_leave_used: number;
    previous_year_advance: number;
    remaining_leave: number;
    remarks: string;
}

interface EditForm {
    staffId: number;
    staffName: string;
    annual_leave_generated: number;
    annual_leave_used: number;
    previous_year_advance: number;
}

const AnnualLeaveManagementPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState<AnnualLeaveData[]>([]);
    const [filteredEmployees, setFilteredEmployees] = useState<AnnualLeaveData[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState<EditForm>({
        staffId: 0,
        staffName: '',
        annual_leave_generated: 0,
        annual_leave_used: 0,
        previous_year_advance: 0
    });
    const [saving, setSaving] = useState(false);
    const [recalculating, setRecalculating] = useState(false);

    useEffect(() => {
        fetchAnnualLeaveData();
    }, []);

    useEffect(() => {
        filterEmployees();
    }, [searchTerm, employees]);

    const fetchAnnualLeaveData = async () => {
        setLoading(true);
        try {
            const data = await StaffApis.getAnnualLeaveList();
            setEmployees(data);
            setFilteredEmployees(data);
        } catch (error) {
            console.error('Failed to fetch annual leave data:', error);
            toast.error('연차 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const filterEmployees = () => {
        let filtered = [...employees];

        // 검색어 필터
        if (searchTerm) {
            filtered = filtered.filter(emp =>
                emp.name.includes(searchTerm) ||
                emp.position.includes(searchTerm)
            );
        }

        setFilteredEmployees(filtered);
    };

    const handleEdit = (employee: AnnualLeaveData) => {
        setEditForm({
            staffId: employee.id,
            staffName: employee.name,
            annual_leave_generated: employee.annual_leave_generated,
            annual_leave_used: employee.annual_leave_used,
            previous_year_advance: employee.previous_year_advance
        });
        setShowEditModal(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await StaffApis.updateAnnualLeave(editForm.staffId, {
                annual_leave_generated: editForm.annual_leave_generated,
                annual_leave_used: editForm.annual_leave_used,
                previous_year_advance: editForm.previous_year_advance
            });
            toast.success('연차 정보가 수정되었습니다.');
            setShowEditModal(false);
            fetchAnnualLeaveData();
        } catch (error) {
            console.error('Failed to update annual leave:', error);
            toast.error('연차 정보 수정에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleRecalculate = async () => {
        if (!window.confirm('모든 직원의 연차를 입사일 기준으로 재계산하시겠습니까?')) {
            return;
        }

        setRecalculating(true);
        try {
            const result = await StaffApis.recalculateAllAnnualLeaves();
            toast.success(`${result.updated}명의 연차가 재계산되었습니다.`);
            fetchAnnualLeaveData();
        } catch (error) {
            console.error('Failed to recalculate annual leaves:', error);
            toast.error('연차 재계산에 실패했습니다.');
        } finally {
            setRecalculating(false);
        }
    };

    const columns: Column<AnnualLeaveData>[] = [
        {
            key: 'name',
            header: '이름',
            width: '100px',
            render: (value) => value || '-'
        },
        {
            key: 'position',
            header: '직책',
            width: '100px',
            render: (value) => value || '-'
        },
        {
            key: 'join_date',
            header: '입사일',
            width: '120px',
            render: (value) => value ? moment(value).format('YYYY-MM-DD') : '-'
        },
        {
            key: 'annual_leave_generated',
            header: '발생연차',
            width: '80px',
            align: 'center',
            render: (value) => value || 0
        },
        {
            key: 'annual_leave_used',
            header: '소진연차',
            width: '80px',
            align: 'center',
            render: (value) => value || 0
        },
        {
            key: 'previous_year_advance',
            header: '전년도 가불',
            width: '100px',
            align: 'center',
            render: (value) => value || 0
        },
        {
            key: 'remaining_leave',
            header: '잔여연차',
            width: '80px',
            align: 'center',
            render: (value) => {
                const className = value <= 3 ? 'text-danger' : value <= 5 ? 'text-warning' : '';
                return <span className={className}>{value || 0}</span>;
            }
        },
        {
            key: 'remarks',
            header: '비고',
            width: '150px',
            render: (value) => value || '-'
        }
    ];

    // 통계 계산
    const stats = {
        totalEmployees: employees.length,
        totalGenerated: employees.reduce((sum, emp) => sum + emp.annual_leave_generated, 0),
        totalUsed: employees.reduce((sum, emp) => sum + emp.annual_leave_used, 0),
        totalRemaining: employees.reduce((sum, emp) => sum + emp.remaining_leave, 0)
    };

    if (loading) {
        return (
            <PageContainer>
                <div className="loading-container">
                    <Loading text="연차 정보를 불러오는 중..." />
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="연차 관리"
                subtitle="직원 연차 현황 및 관리"
                actions={
                    <Button
                        className="btn-secondary"
                        onClick={handleRecalculate}
                        disabled={recalculating}
                    >
                        {recalculating ? '재계산 중...' : '연차 재계산'}
                    </Button>
                }
            />

            <StatsGrid>
                <StatCard
                    title="총 직원수"
                    value={stats.totalEmployees}
                    suffix="명"
                    iconColor="total"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                    </svg>}
                />
                <StatCard
                    title="총 발생연차"
                    value={stats.totalGenerated}
                    suffix="일"
                    iconColor="primary"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                    </svg>}
                />
                <StatCard
                    title="총 소진연차"
                    value={stats.totalUsed}
                    suffix="일"
                    iconColor="warning"
                    valueColor="warning"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                    </svg>}
                />
                <StatCard
                    title="총 잔여연차"
                    value={stats.totalRemaining}
                    suffix="일"
                    iconColor="success"
                    valueColor="success"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                    </svg>}
                />
            </StatsGrid>

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="이름 또는 직책으로 검색..."
                />
            </FilterSection>

            <TableContainer
                title="연차 현황"
                resultCount={filteredEmployees.length}
            >
                <Table
                    columns={columns}
                    data={filteredEmployees}
                    onRowClick={(employee) => handleEdit(employee)}
                    emptyMessage="직원 정보가 없습니다."
                />
            </TableContainer>

            {/* 수정 모달 */}
            <Modal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                title={`연차 정보 수정 - ${editForm.staffName}`}
            >
                <div className="modal-form">
                    <div className="form-group">
                        <label>발생연차</label>
                        <Input
                            type="number"
                            value={editForm.annual_leave_generated}
                            onChange={(e) => setEditForm({
                                ...editForm,
                                annual_leave_generated: Number(e.target.value)
                            })}
                        />
                    </div>
                    <div className="form-group">
                        <label>소진연차</label>
                        <Input
                            type="number"
                            value={editForm.annual_leave_used}
                            onChange={(e) => setEditForm({
                                ...editForm,
                                annual_leave_used: Number(e.target.value)
                            })}
                        />
                    </div>
                    <div className="form-group">
                        <label>전년도 가불</label>
                        <Input
                            type="number"
                            value={editForm.previous_year_advance}
                            onChange={(e) => setEditForm({
                                ...editForm,
                                previous_year_advance: Number(e.target.value)
                            })}
                        />
                    </div>
                    <div className="form-group">
                        <label>잔여연차 (자동계산)</label>
                        <div className="calculated-value">
                            {editForm.annual_leave_generated - editForm.annual_leave_used - editForm.previous_year_advance}일
                        </div>
                    </div>
                    <div className="modal-footer">
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? '저장 중...' : '저장'}
                        </Button>
                        <Button
                            className="btn-cancel"
                            onClick={() => setShowEditModal(false)}
                        >
                            취소
                        </Button>
                    </div>
                </div>
            </Modal>
        </PageContainer>
    );
};

export default AnnualLeaveManagementPage;